import React, { ReactNode, useEffect, useRef, useState } from 'react';
import {
  Box,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  InputAdornment,
  IconButton,
  Button,
  SelectChangeEvent,
  useMediaQuery,
  useTheme,
  Drawer,
  Typography,
  Badge,
} from '@mui/material';
import { 
  Search as SearchIcon, 
  RestartAlt as ResetIcon, 
  Clear as ClearIcon,
  FilterList as FilterIcon,
  Close as CloseIcon 
} from '@mui/icons-material';

export interface FilterOption<T = string | number> {
  value: T;
  label: string;
}

export interface FilterConfig<T = any> {
  id: string;
  type: 'text' | 'select' | 'custom';
  label?: string;
  value?: T;
  onChange?: (value: T) => void;
  options?: FilterOption[];
  minWidth?: string;
  flexGrow?: number;
  placeholder?: string;
  /** `change` оновлює значення фільтра І зберігає його в localStorage (як для text/select) - викликати замість прямого onChange. */
  renderCustom?: (change: (value: T) => void) => ReactNode;
  multiple?: boolean;
  allLabel?: string;
}

interface PageToolbarProps {
  filters: FilterConfig[];
  /** Якщо передано — стан фільтрів зберігається в localStorage під цим ключем */
  storageKey?: string;
  onReset?: () => void;
  showReset?: boolean;
  extraActions?: ReactNode;
  containerSx?: object;
  /** Чи ховати додаткові дії на мобайлі (наприклад, якщо вони винесені в StickyMobileAction) */
  hideExtraActionsOnMobile?: boolean;
}

export const PageToolbar: React.FC<PageToolbarProps> = ({
  filters = [],
  storageKey,
  onReset,
  showReset = true,
  extraActions,
  containerSx,
  hideExtraActionsOnMobile = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  useEffect(() => {
    if (!storageKey) return;
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return;
      const saved: Record<string, any> = JSON.parse(raw);
      filtersRef.current.forEach((f) => {
        if (f.id in saved && f.onChange) {
          f.onChange(saved[f.id]);
        }
      });
    } catch { /* ignore */ }
  }, [storageKey]);

  const persist = (filterId: string, value: any) => {
    if (!storageKey) return;
    try {
      const raw = localStorage.getItem(storageKey);
      const saved: Record<string, any> = raw ? JSON.parse(raw) : {};
      saved[filterId] = value;
      localStorage.setItem(storageKey, JSON.stringify(saved));
    } catch { /* ignore */ }
  };

  const handleChange = (filter: FilterConfig, value: any) => {
    persist(filter.id, value);
    filter.onChange?.(value);
  };

  const isFilterActive = (f: FilterConfig) => {
    if (f.type === 'text') return !!f.value;
    if (f.type === 'select') return Array.isArray(f.value) ? f.value.length > 0 : !!f.value;
    if (f.type === 'custom') return !!f.value;
    return false;
  };

  const activeFiltersCount = filters.filter((f) => f.id !== 'search' && isFilterActive(f)).length;
  const hasActiveFilters = filters.some(isFilterActive);

  const handleReset = () => {
    if (storageKey) {
      try { localStorage.removeItem(storageKey); } catch { /* ignore */ }
    }
    onReset?.();
  };

  const renderFilter = (filter: FilterConfig, inDrawer = false) => {
    if (filter.type === 'custom') {
      return filter.renderCustom ? (
        <React.Fragment key={filter.id}>
          {filter.renderCustom((value) => handleChange(filter, value))}
        </React.Fragment>
      ) : null;
    }

    if (filter.type === 'text') {
      return (
        <TextField
          key={filter.id}
          value={filter.value}
          onChange={(e) => handleChange(filter, e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: filter.value ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => handleChange(filter, '')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
            sx: {
              minWidth: 0,
              '& .MuiInputBase-input': {
                minWidth: 0,
                width: '100%',
              }
            }
          }}
          sx={{ 
            flexGrow: filter.flexGrow ?? 1, 
            minWidth: 0,
            width: inDrawer ? '100%' : (isMobile ? '10px' : (filter.minWidth ?? '120px')),
          }}
          size="small"
          placeholder={filter.placeholder ?? filter.label}
        />
      );
    }

    if (filter.type === 'select') {
      const allCount = filter.options?.length ?? 0;
      const isMultiple = filter.multiple ?? false;

      const multiRenderValue = (selected: any[]) => {
        if (!selected.length) return '';
        if (selected.length === allCount) return filter.allLabel ?? 'Всі';
        return `${selected.length} з ${allCount}`;
      };

      return (
        <FormControl
          key={filter.id}
          sx={{
            minWidth: inDrawer ? '100%' : (isMultiple && !isMobile ? 0 : (isMobile ? '0px' : (filter.minWidth ?? '120px'))),
            flexGrow: inDrawer ? 0 : (filter.flexGrow ?? 0),
            width: isMultiple && !inDrawer && !isMobile ? 'auto' : undefined,
          }}
          size="small"
        >
          <InputLabel>{filter.label}</InputLabel>
          <Select
            value={filter.value}
            label={filter.label}
            multiple={isMultiple}
            onChange={(e: SelectChangeEvent<any>) => handleChange(filter, e.target.value)}
            renderValue={isMultiple ? multiRenderValue : undefined}
          >
            {filter.options?.map((opt) => (
              <MenuItem key={String(opt.value)} value={opt.value}>
                {isMultiple && (
                  <Checkbox
                    size="small"
                    checked={Array.isArray(filter.value) && filter.value.includes(opt.value)}
                    sx={{ py: 0 }}
                  />
                )}
                <ListItemText primary={opt.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    return null;
  };

  const searchFilter = filters.find(f => f.id === 'search');
  const otherFilters = filters.filter(f => f.id !== 'search');

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          gap: 1.0,
          alignItems: 'center',
          flexWrap: isMobile ? 'nowrap' : 'wrap',
          width: '100%',
          overflow: 'hidden',
          ...containerSx,
        }}
      >
        {isMobile ? (
          <>
            {searchFilter ? renderFilter(searchFilter) : <Box sx={{ flexGrow: 1 }} />}
            <Badge badgeContent={activeFiltersCount} color="primary" sx={{ ml: 0.5, flexShrink: 0 }}>
              <IconButton 
                onClick={() => setIsDrawerOpen(true)}
                sx={{ 
                  bgcolor: activeFiltersCount > 0 ? 'action.selected' : 'transparent',
                  border: '1px solid',
                  borderColor: 'divider',
                  p: isSmallMobile ? '5px' : '7px'
                }}
              >
                <FilterIcon fontSize={isSmallMobile ? "small" : "medium"} />
              </IconButton>
            </Badge>
          </>
        ) : (
          <>
            {filters.map(f => renderFilter(f))}
            {extraActions && (
              <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                {extraActions}
              </Box>
            )}
          </>
        )}

        {isMobile && !hideExtraActionsOnMobile && extraActions && (
          <Box sx={{ ml: 0.5, flexShrink: 0 }}>{extraActions}</Box>
        )}

        {!isMobile && showReset && onReset && hasActiveFilters && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleReset}
            startIcon={<ResetIcon fontSize="small" />}
            sx={{ height: '40px', whiteSpace: 'nowrap', ml: extraActions ? 1 : 'auto' }}
          >
            Скинути
          </Button>
        )}
      </Box>

      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        PaperProps={{
          sx: { width: '85%', maxWidth: 360, p: 3 }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Фільтри</Typography>
          <IconButton onClick={() => setIsDrawerOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {otherFilters.map(f => renderFilter(f, true))}
        </Box>

        <Box sx={{ mt: 'auto', pt: 4, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Button 
            variant="contained" 
            fullWidth 
            size="large"
            onClick={() => setIsDrawerOpen(false)}
          >
            Застосувати
          </Button>
          {showReset && (
            <Button 
              variant="outlined" 
              color="secondary" 
              fullWidth
              onClick={() => { handleReset(); setIsDrawerOpen(false); }}
            >
              Очистити все
            </Button>
          )}
        </Box>
      </Drawer>

      <Divider sx={{ mx: -2, mt: 2, mb: 2 }} />
    </>
  );
};
